import Modal from 'component-index/Modal'
import PageContent from 'component-index/PageContent'
import Prompt from 'component-index/Prompt'
import { createBrowserHistory } from 'history'
import { computed, makeObservable, runInAction } from 'mobx'
import { observer } from 'mobx-react'
import React, { Fragment } from 'react'
import ReactDOM from 'react-dom'
import { BrowserRouter as Router, Route, Switch, withRouter } from 'react-router-dom'
import Sticky from 'react-stickynode'
import { CSSTransition, SwitchTransition } from 'react-transition-group'
import TetheredPopover from '../components/TetheredPopover'
import DataSourceFactory from '../store/models/datasource-factory'
import { uistore } from '../store/uistore'
import errorToast from './error-toast'
import { ComponentProvider } from './renderer/Context'
import Grid from './renderer/Grid'
import GridItem from './renderer/GridItem'
import PortalComponent from './renderer/PortalComponent'
import ScrollToTopOnMount from './ScrollToTop'
import inject from './mobx-inject'
import Drawer from 'component-index/Drawer'

const _PageRoute = observer(class _PageRoute extends React.Component {
  render () {
    const { route, modal,  isModal, isPopover, location, rootPath } = this.props
    if (!route || (!modal && ((isModal || isPopover) && route.matchesPath(location.pathname)))) {
      return <div />
    }

    if (!route.page) return null

    return <Page rootPath={rootPath} page={route.page} route={route} isModal={modal} />
  }
})

const PageRoute = withRouter(_PageRoute)

const Content = withRouter(observer(class Content extends React.Component {
  back = () => {
    this.props.history.replace(this.state.nonModalLocation)
  }

  constructor (nextProps) {
    super(nextProps)
    this.state = {
      nonModalLocation: nextProps.history.location
    }
    this.state = {
      ...(this.state ?? {}),
      isModal: nextProps.location?.state && nextProps.state?.modal,
      isDrawer: nextProps.location?.state && nextProps.state?.drawer,
      isPopover: nextProps.location?.state && nextProps.state?.popover,
    }
  }

  static getDerivedStateFromProps (nextProps, state) {
    const { location } = nextProps
    const newState = { ...state }

    // set previousLocation if props.location is not modal-like
    const hasModalState = location.state?.modal || location.state?.popover || location.state?.drawer
    if (nextProps.history && !hasModalState) {
      newState.nonModalLocation = nextProps.history.location
    }

    const isModalLike =
      nextProps.location?.state &&
        state.nonModalLocation !== nextProps.history.location
    const isPopover = isModalLike && nextProps.location.state.popover
    const isModal = isModalLike && nextProps.location.state.modal
    const isDrawer = isModalLike && nextProps.location.state.drawer

    let currentLocation = state.currentLocation
    let previousLocation = state.previousLocation
    const nextLocation = (isModal || isPopover || isDrawer) ? newState.nonModalLocation : nextProps.history.location

    if (!currentLocation || (nextLocation.pathname !== currentLocation.pathname || nextLocation.search !== currentLocation.search || nextLocation.hash !== currentLocation.hash)) {
      currentLocation = nextLocation
      previousLocation = state.currentLocation
      runInAction(() => { uistore.contentRoute = currentLocation })
    }

    return {
      ...newState,
      isModal,
      isPopover,
      isDrawer,
      currentLocation,
      previousLocation,
      isDrawerOpen: true,
    }
  }
  setDrawerOpen = (isOpen) => {
    if (isOpen && !this.state.isDrawerOpen) {
      this.setState({ isDrawerOpen: true })
    } else if (!isOpen && this.state.isDrawerOpen) {
      this.setState({ isDrawerOpen: false })
      this.back()
    }
  }

  onPopoverClose = () => {
    if(this.state.isPopover) {
      this.props.history.replace({
        ...this.state.nonModalLocation,
        state: { popoverClose: true }
      })
    }
  }

  render() {
    const { location, routes = [], rootPath, history } = this.props
    const { isPopover, isModal } = this.state
    const transitions = this.props.transitions || {};

    const poppedOverComp = (isPopover && (
      <PageRoute
        rootPath={rootPath}
        route={routes.find(r => r.matchesPath(location.pathname, true))}
        isPopover
        modal
      />
    )) || null

    const contentLocation = this.state.currentLocation

    const rts = routes?.filter(r => isModal || isPopover
      ? !r.matchesPath(location.pathname, true)
      : true
    )

      .map(r => (
        <Route
          location={contentLocation}
          exact
          key={r.cleanPath}
          path={r.cleanPath}>
          {props => (props.match !== null && <PageRoute route={r} rootPath={rootPath} />)}
        </Route>

      ))

    const switchedRoutes = (
      <Switch location={contentLocation}>
        {rts}
      </Switch>
    );

    const hasTransitions = transitions.enter || transitions.exit;
    const routesWithTransitions = hasTransitions && (
      <SwitchTransition mode="out-in">
        <CSSTransition key={location.pathname} classNames={{
          enter: `animate__animated animate__${transitions.enter}`,
          exit: `animate__animated animate__${transitions.exit}`,
        }} timeout={transitions.duration || 0} unmountOnExit >
          {switchedRoutes}
        </CSSTransition>
      </SwitchTransition>
    );

    return (
      <Fragment>
        { hasTransitions ? routesWithTransitions :  switchedRoutes }
        {(this.state.isModal || this.state.isDrawer) && routes.filter(r => r.matchesPath(history.location.pathname)).map(r => (
          <Route
            location={history.location}
            exact
            key={r.cleanPath}
            path={r.cleanPath}
            render={() => {
              if (this.state.isModal) {
                return (
                  <Modal
                    isOpen
                    stopPropagationOnClose
                    shouldCloseOnEsc={!this.props.isPreview}
                    hideSpinner
                    overlayStyle={{ position: 'fixed', zIndex: '2000' }}
                    contentStyle={{
                      ...(history.location.state || {})
                    }}
                    {...(history.location.modalConfig || {})}
                    onCancel={this.back}
                    bodyText={<PageRoute route={r} rootPath={rootPath} modal isModal />}
                    headerText={(r.label || (history.location.modalConfig && history.location.modalConfig.headerText))}
                    size='large' />
                )
              } else {
                const {isModal, placement, width, height, showCloseButton, closeOnOverlayClick} = location.state.drawer
                return (
                  <Drawer
                    isOpen={this.state.isDrawerOpen}
                    setOpen={this.setDrawerOpen}
                    closeOnOverlayClick={closeOnOverlayClick}
                    showCloseButton={showCloseButton}
                    isModal={isModal}
                    placement={placement}
                    width={width}
                    height={height}
                    animateInitialOpen
                  >
                    <PageRoute route={r} rootPath={rootPath} drawer isDrawer />
                  </Drawer>
                )
              }
            }} />))}
        {!this.state.isDrawer && <Route>
          {props => (
            <TetheredPopover
              {...props.location.state || {}}
              onClose={this.onPopoverClose}
            >
              {poppedOverComp}
            </TetheredPopover>
          )}
        </Route>}
      </Fragment>
    )
  }
}))

const _Layout = observer(class _Layout extends React.Component {
  constructor (props) {
    super(props)

    makeObservable(this, {
      currPage: computed
    })

    this.content = props.layout && props.layout.components && props.layout.components.find(
      c => c.type === 'PageContent' || c.type === 'Content'
    )
    if (!this.content) {
      errorToast(
        'No PageContent Component: ',
        'PageContent component required in layout.'
      )
    }
    this.routes = props.routes && props.routes.filter(
      c => !c.externalRoute && !(c.href == null && c.label == null)
    )

    runInAction(() => { uistore.contentRoute = this.props.history.location })    
        
    this.load()
  }

  load = () => {
    if(typeof window === 'undefined') return;
    if (this.props.layout) {
      return uistore.dataSourceFactory.loadData(
        this.props.layout.completeFieldSet,
        this.props
      )
    }
  }

  get currPage () {
    if (!uistore.contentRoute) return
    const r = this.props.routes && this.props.routes.find(r => r.matchesPath(uistore.contentRoute.pathname, true))
    if (r) return r.page
  }

  render () {
    const { layout, theme, isPreview, location } = this.props
    layout.toProps
    const currPage = this.currPage
    return (
      <Grid
        className='Layout'
        style={{ width: '100%' }}
        settings={this.props.store.settings}
        compactType={layout && layout.compactType}
        maxY={layout && layout.maxY}
      >
        {theme && theme.css && isPreview && (
          <style dangerouslySetInnerHTML={{ __html: theme.css }} />
        )}
        {this.content &&
          layout.componentsOrderedLtr.map(comp => {
            let position = comp.position
            const contentHeight =
              currPage && currPage.maxY > this.content.position.h
                ? currPage.maxY
                : this.content.position.h
            if (comp.type === 'PageContent' || comp.type === 'Content') {
              let compProps = comp.toProps
              {/* comp.mapping?.children?.forEach(compProp => {
                  const { value, key, children } = compProp
                  compProps = {
                  [key]:
                  children?.length > 0
                  ? Object.assign(
                  {},
                  ...children.map(nested => ({
                  [nested.key]: nested.value
                  }))
                  )
                  : value,
                  ...compProps
                  }
                  })
                  console.log(compProps) */}
              //             let gridRow = `${comp.position.y + 1} / span ${contentHeight}`
              //             if(comp.compactType) gridRow = 'auto'
              return (
                <GridItem
                  key={comp.id}
                  settings={this.props.store.settings}
                  position={{ ...position }}
                  compactType={layout.compactType}
                  style={!layout.compactType && { gridRow: `${comp.position.y + 1} / span ${contentHeight + 3}` }}
                >
                  <PageContent
                    {...compProps}
                    {...this.props}
                    routes={this.routes}
                    StickyWrapper={Sticky}
                  >
                    <Content transitions={compProps.animated && compProps.transitions} {...this.props} routes={this.routes} />
                  </PageContent>
                </GridItem>
              )
            } else {
              if (
                currPage &&
                position.y >=
                this.content.position.y + this.content.position.h &&
                position.y < contentHeight
              ) {
                position = {
                  ...position,
                  y: contentHeight + position.y - this.content.position.h
                }
              }
              return (
                <PortalComponent
                  key={comp.id}
                  component={comp}
                  position={position}
                  location={location}
                />
              )
            }
          })}
      </Grid>
    )
  }
})

export const Layout = withRouter(_Layout)

const _Page = inject('store')(observer(class _Page extends React.Component {
  constructor(props) {
    super(props)
    if (!this.props.noAutoLoad) this.load()
  }

  load = () => {
    if(typeof window === 'undefined') return;

    return uistore.dataSourceFactory.loadData(
      this.props.page.completeFieldSet,
      this.props,
      true
    )
  }

  setPageTitle = () => {
    const { route, match } = this.props
    const siteTitle = this.props.store.$treenode?.environment?.sitePages?.[location.pathname.toLowerCase()]?.title
    if (siteTitle) {
      document.title = siteTitle
    } else if (route) {
      let title = Object.keys(match.params).reduce((res, paramName) => res.replaceAll(':' + paramName, match.params[paramName]), route.title || '')
      document.title = title || route.label || route.page.name
    }
  }

  componentDidMount() {
    uistore.setRouter(this.props)
    this.setPageTitle()
  }

  componentDidUpdate() {
    uistore.setRouter(this.props)
    this.setPageTitle()
  }

  render () {
    const { page } = this.props
    page.toProps
    return (
      <Grid id={'ivr_' + page.id} settings={this.props.store.settings} compactType={page.compactType} maxY={page.maxY}>
        {!this.props.isModal && <ScrollToTopOnMount />}
        {page.componentsOrderedLtr && page.componentsOrderedLtr.map(comp => (
          <PortalComponent
            key={comp.id}
            history={this.props.history}
            rootPath={this.props.rootPath}
            component={comp}
            settings={this.props.store.settings}
          />
        ))}
      </Grid>
    )
  }
}))

export const Page = withRouter(_Page)

let MAX_W = 48
let ROW_HEIGHT = 20

export default class Renderer extends React.Component {
  constructor (props) {
    super(props)
    MAX_W = props.store.settings.MAX_W || MAX_W
    ROW_HEIGHT = props.store.settings.ROW_HEIGHT || ROW_HEIGHT
    this.history = createBrowserHistory({
      basename: this.props.rootPath || ''
    })
    uistore.setDataSourceFactory(
      uistore.dataSourceFactory || new DataSourceFactory(props.store.currentApp, { history: this.history })
    )
  }

  render () {
    const { isPreview, store, layout, navigation, theme, rootPath } = this.props
    return (
      <Router
        basename={rootPath}
        history={this.history}
        getUserConfirmation={(message, callback) => {
          const promptContainer = document.createElement('div')
          promptContainer.setAttribute('id', 'promptContainer')
          document.body.appendChild(promptContainer)
          ReactDOM.render((
            <Prompt message={message} callback={callback} cancelLabel={'Go back'} confirmLabel={'Continue'} />
          ), document.getElementById('promptContainer'))
        }}
      >
        <ComponentProvider routes={navigation?.routes} history={this.history} store={store}>
          <Layout
            history={this.history}
            rootPath={rootPath}
            store={store}
            layout={layout}
            routes={navigation && navigation.routes}
            theme={theme}
            isPreview={isPreview}
          />
        </ComponentProvider>
      </Router>
    )
  }
}
